import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Send, RefreshCw, ExternalLink, Copy, Search } from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import { getGrowthInvitations } from '../../Services/GrowthService';
import { GrowthInvitation } from '../../Types/GrowthType';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import Pagination from '../../../Shared/Pagination/Pagination';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';

const PAGE_SIZE = 10;

const INVITATION_STATUSES = ['CREATED', 'SENT', 'DELIVERED', 'OPENED', 'CLICKED', 'ACCEPTED', 'REGISTERED', 'EXPIRED'];

const headerClass =
  'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

const labelClass =
  'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const statusBadge = (status?: string | null) => {
  const s = (status || '').toUpperCase();
  if (s === 'ACCEPTED' || s === 'REGISTERED')
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
  if (s === 'EXPIRED') return 'border-rose-500/30 bg-rose-500/10 text-rose-400';
  if (s === 'OPENED' || s === 'CLICKED')
    return 'border-amber-500/30 bg-amber-500/10 text-amber-400';
  return 'border-slate-600/40 bg-slate-700/20 text-slate-300';
};

function GrowthInvitationsComponent() {
  const [pageData, setPageData] = useState<PaginationResponse<GrowthInvitation> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGrowthInvitations({
        page,
        size: PAGE_SIZE,
        search: searchFilter || undefined,
        status: statusFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      if (response.success && response.data) {
        setPageData(response.data);
      }
    } catch (err) {
      console.error('Failed to load invitations', err);
    } finally {
      setLoading(false);
    }
  }, [page, searchFilter, statusFilter, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [searchFilter, statusFilter, fromDate, toDate]);

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setAlert({ variant: 'success', title: 'Copied', message: 'Invitation link copied to clipboard.', autoCloseMs: 3000, onClose: () => setAlert(null) });
    } catch {
      setAlert({ variant: 'error', title: 'Copy Failed', message: 'Unable to copy the link.', autoCloseMs: 3000, onClose: () => setAlert(null) });
    }
  };

  const content = pageData?.content || [];
  const totalElements = pageData?.totalElements || 0;
  const totalPages = pageData?.totalPages || 0;

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <GrowthTabNav />

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Send size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Growth Invitations</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Invitation links sent to prospects
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search by email / prospect name..."
              className={`${fieldClass} pl-9`}
            />
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={fieldClass}>
              <option value="">All statuses</option>
              {INVITATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={fieldClass} />
          </div>
          <div>
            <label className={labelClass}>To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={fieldClass} />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
        <table className="w-full min-w-[1000px] text-left">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              {['Prospect', 'Email', 'Status', 'Opened', 'Clicked', 'Accepted', 'Expires', 'Link', 'Created'].map((h) => (
                <th key={h} className={headerClass}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {content.map((inv, idx) => (
              <motion.tr
                key={inv.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="transition hover:bg-slate-900/70"
              >
                <td className="px-5 py-4 text-sm font-bold text-slate-100">{inv.prospectName || '—'}</td>
                <td className="px-5 py-4 text-sm font-semibold text-cyan-400">{inv.email || '—'}</td>
                <td className="px-5 py-4">
                  <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(inv.status)}`}>
                    {inv.status}
                  </span>
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">{inv.openedAt ? formatDateTime(inv.openedAt) : '—'}</td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">{inv.clickedAt ? formatDateTime(inv.clickedAt) : '—'}</td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">{inv.acceptedAt ? formatDateTime(inv.acceptedAt) : '—'}</td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-500">{inv.expiresAt ? formatDateTime(inv.expiresAt) : '—'}</td>
                <td className="px-5 py-4">
                  {inv.invitationUrl ? (
                    <div className="flex items-center gap-1.5">
                      <span className="max-w-[140px] truncate text-xs font-semibold text-slate-300">{inv.invitationUrl}</span>
                      <button
                        onClick={() => copyLink(inv.invitationUrl || '')}
                        title="Copy link"
                        className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-1.5 text-cyan-400 transition hover:bg-cyan-500/20"
                      >
                        <Copy size={13} />
                      </button>
                      <a
                        href={inv.invitationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open link"
                        className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-1.5 text-sky-400 transition hover:bg-sky-500/20"
                      >
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-500">{formatDateTime(inv.createdAt)}</td>
              </motion.tr>
            ))}
            {content.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  No invitations sent yet.
                </td>
              </tr>
            )}
            {loading && content.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  <RefreshCw className="mx-auto h-6 w-6 animate-spin text-cyan-500" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalElements > 0 && (
        <div className="mt-2 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={page}
            size={PAGE_SIZE}
            totalElements={totalElements}
            totalPages={totalPages}
            first={pageData?.first ?? page === 0}
            last={pageData?.last ?? false}
            hasNext={pageData?.hasNext ?? false}
            hasPrevious={pageData?.hasPrevious ?? page > 0}
            setPage={setPage}
          />
        </div>
      )}
    </div>
  );
}

export default GrowthInvitationsComponent;